<script setup lang="ts">
type RulesDetailIcon =
  | "message-circle"
  | "wallet"
  | "map-pin"
  | "arrow-down-right";

interface RulesDetailItem {
  icon: RulesDetailIcon;
  subheader: string;
  body: string;
  href?: string;
}

interface RulesDetailBlockData {
  eyebrow?: string;
  header: string;
  intro?: string;
  image?: string;
  image_alt?: string;
  video_src?: string;
  video_src_webm?: string;
  video_poster?: string;
  items: RulesDetailItem[];
}

defineProps<{
  block: RulesDetailBlockData;
}>();
</script>

<template>
  <section
    class="relative overflow-hidden bg-slate-50 dark:bg-slate-900/40"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />

    <div class="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:px-8 md:py-12">
      <header data-reveal class="mb-8 max-w-3xl md:mb-10">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          class="mt-3 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-4 font-body text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div class="grid gap-8 md:grid-cols-12 md:gap-12 md:items-stretch">
        <figure
          data-reveal
          data-reveal-delay="1"
          class="md:col-span-5 md:order-1 md:h-auto lg:h-full"
        >
          <div
            class="relative h-64 w-full overflow-hidden rounded-2xl shadow-lg sm:h-80 md:h-80 lg:h-full lg:min-h-[24rem]"
          >
            <video
              v-if="block.video_src"
              class="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
              :poster="block.video_poster ?? block.image"
              :aria-label="block.image_alt ?? block.header"
              autoplay
              muted
              loop
              playsinline
              preload="metadata"
            >
              <source
                v-if="block.video_src_webm"
                :src="block.video_src_webm"
                type="video/webm"
              />
              <source :src="block.video_src" type="video/mp4" />
              <track kind="captions" srclang="en" label="No captions" src="/captions/empty.vtt" default>
            </video>
            <NuxtImg
              v-if="block.image"
              :src="block.image"
              :alt="block.image_alt ?? block.header"
              width="1400"
              height="1050"
              format="webp"
              loading="lazy"
              sizes="100vw md:42vw"
              class="absolute inset-0 h-full w-full object-cover"
              :class="block.video_src ? 'hidden motion-reduce:block' : ''"
            />
            <div
              aria-hidden="true"
              class="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-slate-950/25 to-transparent"
            />
            <div
              aria-hidden="true"
              class="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-slate-950/25 to-transparent"
            />
          </div>
        </figure>

        <div
          class="md:col-span-7 md:order-2 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-1 md:gap-4 lg:grid-cols-2"
        >
          <template
            v-for="(item, i) in block.items"
            :key="item.icon + item.subheader"
          >
            <a
              v-if="item.href"
              :href="item.href"
              :data-reveal="''"
              :data-reveal-delay="String(i + 2)"
              :aria-label="`${item.subheader}: ${item.body}`"
              class="group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-amber-200/70 bg-white/85 p-5 shadow-sm backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-amber-300 hover:bg-white hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:p-6 dark:border-amber-500/20 dark:bg-slate-900/80 dark:hover:border-amber-400/40 dark:hover:bg-slate-900 dark:focus-visible:ring-offset-slate-900"
            >
<div class="flex items-start justify-between gap-4">
                <span
                  class="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 ring-1 ring-amber-200/70 shadow-inner transition-transform duration-300 group-hover:-translate-y-0.5 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/20"
                >
                  <Icon
                    :name="`lucide:${item.icon}`"
                    class="size-6"
                    aria-hidden="true"
                  />
                </span>
              </div>
              <div class="min-w-0">
                <h3
                  class="flex items-center gap-2 font-display text-xl italic leading-tight text-slate-900 md:text-2xl dark:text-white"
                >
                  <span class="break-words">{{ item.subheader }}</span>
                  <Icon
                    name="lucide:arrow-down"
                    class="size-5 shrink-0 text-amber-600 transition-transform duration-300 group-hover:translate-y-0.5 dark:text-amber-400"
                    aria-hidden="true"
                  />
                </h3>
                <div class="mt-2 h-px w-10 bg-amber-300/80 transition-all duration-300 group-hover:w-16 dark:bg-amber-400/60" />
                <p
                  class="mt-3 font-body text-base leading-relaxed text-slate-700 dark:text-slate-300"
                >
                  {{ item.body }}
                </p>
              </div>
            </a>
            <article
              v-else
              :data-reveal="''"
              :data-reveal-delay="String(i + 2)"
              class="group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-amber-200/70 bg-white/85 p-5 shadow-sm backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-amber-300 hover:bg-white hover:shadow-xl sm:p-6 dark:border-amber-500/20 dark:bg-slate-900/80 dark:hover:border-amber-400/40 dark:hover:bg-slate-900"
            >
<div class="flex items-start justify-between gap-4">
                <span
                  class="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 ring-1 ring-amber-200/70 shadow-inner dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/20"
                >
                  <Icon
                    :name="`lucide:${item.icon}`"
                    class="size-6"
                    aria-hidden="true"
                  />
                </span>
              </div>
              <div class="min-w-0">
                <h3
                  class="font-display text-xl italic leading-tight text-slate-900 md:text-2xl dark:text-white break-words"
                >
                  {{ item.subheader }}
                </h3>
                <div class="mt-2 h-px w-10 bg-amber-300/80 dark:bg-amber-400/60" />
                <p
                  class="mt-3 font-body text-base leading-relaxed text-slate-700 dark:text-slate-300"
                >
                  {{ item.body }}
                </p>
              </div>
            </article>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>
